import React, { useEffect, useRef, useState } from "react";
import { API_URL } from "../../../environment";
import { ligarNaConta } from "./access";
import { carregarFuncionalidades } from "../funcionalidades";

// Botão "Ligar" no card do Kanban (resposta do Eduardo ao handoff, 04/11/2026): fluxo MANUAL e separado do
// mailing. O CRM pede à discadora um link de uso único (POST /discadora/ligacoes → /api/integracao/ligacoes
// com a chave da conta) e abre LITERALMENTE a URL devolvida. A URL tem token: não vai para log nem fica guardada.
// O andamento vem da consulta (aguardando abrir, discando, finalizada...): abrir o link não quer dizer em conversa.
// Só aparece para quem tem um atendente da discadora ligado ao seu usuário (área Discadora › Botão Ligar).

let cacheHabilitado: { chave: string; promessa: Promise<boolean> } | null = null;

const habilitado = (): Promise<boolean> => {
  const g = localStorage.getItem("id_grupo") || "";
  const u = localStorage.getItem("id_user") || "";
  if (!g || !u) return Promise.resolve(false);
  const chave = `${g}:${u}`;
  if (!cacheHabilitado || cacheHabilitado.chave !== chave) {
    const q = new URLSearchParams({ id_grupo: g, id_user: u });
    cacheHabilitado = {
      chave,
      // 05/11: a conta precisa ter "discadora_ligar" ligada na tabela funcionalidade_conta; só então
      // pergunta ao serviço se ESTE usuário tem atendente da discadora.
      promessa: carregarFuncionalidades()
        .then((f) => (ligarNaConta(f) ? fetch(`${API_URL}/discadora/ligar/estado?${q.toString()}`) : null))
        .then((r) => (r && r.ok ? r.json() : null))
        .then((d) => !!d?.habilitado)
        .catch(() => false),
    };
  }
  return cacheHabilitado.promessa;
};

const ROTULO: Record<string, { texto: string; cor: string }> = {
  pedindo: { texto: "pedindo o link…", cor: "#6b7280" },
  aguardando_clique: { texto: "aguardando abrir", cor: "#d97706" },
  preparado: { texto: "preparado", cor: "#2563eb" },
  discando: { texto: "discando", cor: "#2563eb" },
  aguardando_resultado: { texto: "aguardando resultado", cor: "#2563eb" },
  finalizado: { texto: "ligação finalizada", cor: "#16a34a" },
  expirado: { texto: "link expirou", cor: "#6b7280" },
  conflito: { texto: "já pedida", cor: "#dc2626" },
  recusado: { texto: "recusada", cor: "#dc2626" },
  erro: { texto: "erro", cor: "#dc2626" },
  erro_incerto: { texto: "sem resposta da discadora", cor: "#dc2626" },
};

// variante "chat" (05/11/2026): botão no topo da conversa, no tamanho dos outros botões do header.
export const BotaoLigarDiscadora: React.FC<{
  fone: string;
  idTime?: string | number | null;
  variante?: "card" | "chat";
}> = ({ fone, idTime, variante = "card" }) => {
  const [pode, setPode] = useState(false);
  const [estado, setEstado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let vivo = true;
    habilitado().then((v) => vivo && setPode(v));
    return () => {
      vivo = false;
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  if (!pode || !fone) return null;

  const acompanhar = (id: number) => {
    const g = localStorage.getItem("id_grupo") || "";
    const fim = Date.now() + 15 * 60 * 1000;
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/discadora/ligacoes/${id}?id_grupo=${encodeURIComponent(g)}`);
        const d = await r.json();
        if (d?.estado) setEstado(d.estado);
        if (d?.final || Date.now() > fim) {
          if (timer.current) clearInterval(timer.current);
          timer.current = null;
        }
      } catch {
        // tenta de novo no próximo ciclo
      }
    }, 4000);
  };

  const ligar = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (estado === "pedindo") return;
    setErro(null);
    // A aba abre JÁ no clique (aberta depois da resposta, o navegador bloquearia); o endereço chega em seguida.
    const janela = window.open("about:blank", "_blank");
    try {
      if (janela) janela.opener = null;
    } catch {
      // sem acesso ao opener: segue
    }
    setEstado("pedindo");
    try {
      const g = localStorage.getItem("id_grupo") || "";
      const r = await fetch(`${API_URL}/discadora/ligacoes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_grupo: Number(g),
          id_user: Number(localStorage.getItem("id_user")),
          usuario: localStorage.getItem("user_email") || "",
          fone,
          id_time: idTime ?? null,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d?.url) throw new Error(d?.error || `Falha (HTTP ${r.status})`);
      if (janela) janela.location.href = d.url;
      else window.open(d.url, "_blank", "noopener");
      setEstado("aguardando_clique");
      acompanhar(Number(d.id));
    } catch (err: any) {
      if (janela) janela.close();
      setEstado(null);
      setErro(err?.message || "Não consegui pedir a ligação.");
    }
  };

  const r = estado ? ROTULO[estado] || { texto: estado, cor: "#6b7280" } : null;

  if (variante === "chat") {
    return (
      <span className="d-inline-flex align-items-center gap-1 flex-shrink-0" style={{ minWidth: 0 }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
          onClick={ligar}
          disabled={estado === "pedindo"}
          title="Ligar pela discadora: abre o telefone numa aba nova, já com este número"
          style={{ height: 32, whiteSpace: "nowrap", fontWeight: 600 }}
        >
          <i className="ti ti-phone-outgoing" style={{ fontSize: 16 }} />
          <span>{estado === "pedindo" ? "Pedindo…" : "Ligar"}</span>
        </button>
        {r && estado !== "pedindo" && (
          <span style={{ fontSize: 11, color: r.cor, whiteSpace: "nowrap", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}>
            {r.texto}
          </span>
        )}
        {erro && (
          <i className="ti ti-alert-triangle" title={erro} style={{ color: "#dc2626", fontSize: 15, cursor: "pointer" }} onClick={() => window.alert(erro)} />
        )}
      </span>
    );
  }

  return (
    <span className="d-inline-flex align-items-center gap-1" onMouseDown={(e) => e.stopPropagation()} style={{ minWidth: 0 }}>
      <button
        type="button"
        onClick={ligar}
        disabled={estado === "pedindo"}
        title="Ligar pela discadora (abre a ficha no fone do atendente)"
        style={{
          background: "#16a34a14", border: "1px solid #16a34a55", color: "#16a34a", borderRadius: 999,
          fontSize: "10px", fontWeight: 700, padding: "1px 8px", cursor: "pointer", textTransform: "uppercase",
          display: "inline-flex", alignItems: "center", gap: 3,
        }}
      >
        <i className="ti ti-phone-outgoing" />
        Ligar
      </button>
      {r && (
        <span style={{ fontSize: "10px", color: r.cor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.texto}</span>
      )}
      {erro && (
        <i className="ti ti-alert-triangle" title={erro} style={{ color: "#dc2626", fontSize: "12px" }} onClick={() => window.alert(erro)} />
      )}
    </span>
  );
};

export default BotaoLigarDiscadora;
